/**
 * CSRF Token 管理工具
 * 自動獲取和附加 CSRF Token 到請求中
 */

let csrfToken = null

/**
 * 從後端獲取 CSRF Token
 * @returns {Promise<string>} CSRF Token
 */
export async function fetchCSRFToken() {
  try {
    const response = await fetch('/api/csrf-token', {
      method: 'GET',
      credentials: 'include'
    })
    
    if (!response.ok) {
      throw new Error('無法獲取 CSRF Token')
    }
    
    const data = await response.json()
    csrfToken = data.csrf_token
    
    // 存儲到 sessionStorage 以便後續使用
    sessionStorage.setItem('csrf_token', csrfToken)
    
    return csrfToken
  } catch (error) {
    console.error('獲取 CSRF Token 失敗:', error)
    return null
  }
}

/**
 * 獲取當前的 CSRF Token（從記憶體或 sessionStorage）
 * @returns {string|null} CSRF Token
 */
export function getCSRFToken() {
  if (csrfToken) {
    return csrfToken
  }
  
  // 從 sessionStorage 讀取
  const storedToken = sessionStorage.getItem('csrf_token')
  if (storedToken) {
    csrfToken = storedToken
    return csrfToken
  }
  
  return null
}

/**
 * 清除 CSRF Token（登出時使用）
 */
export function clearCSRFToken() {
  csrfToken = null
  sessionStorage.removeItem('csrf_token')
}

/**
 * 確保有可用的 CSRF Token
 * @returns {Promise<string>} CSRF Token
 */
export async function ensureCSRFToken() {
  let token = getCSRFToken()
  
  if (!token) {
    token = await fetchCSRFToken()
  }
  
  return token
}

/**
 * 自動附加 CSRF Token 的 fetch 包裝器
 * @param {string} url - 請求 URL
 * @param {object} options - Fetch 選項
 * @returns {Promise<Response>} Fetch Response
 */
export async function secureFetch(url, options = {}) {
  // 確保有 CSRF Token
  await ensureCSRFToken()
  
  // 對於需要 CSRF 保護的方法（POST, PUT, DELETE, PATCH）
  const methodsRequiringCSRF = ['POST', 'PUT', 'DELETE', 'PATCH']
  const method = (options.method || 'GET').toUpperCase()
  
  if (methodsRequiringCSRF.includes(method)) {
    const token = getCSRFToken()
    
    if (token) {
      // 添加 CSRF Token 到 headers
      options.headers = {
        ...options.headers,
        'X-CSRFToken': token
      }
    }
  }
  
  // 確保包含憑證（cookies）
  options.credentials = options.credentials || 'include'
  
  try {
    const response = await fetch(url, options)
    
    // 如果返回 400 且提示 CSRF token missing，重新獲取 token 並重試
    if (response.status === 400) {
      const data = await response.clone().json()
      if (data.message && data.message.includes('CSRF')) {
        console.warn('CSRF token 失效，重新獲取...')
        clearCSRFToken()
        await fetchCSRFToken()
        
        // 重試請求
        const newToken = getCSRFToken()
        if (newToken) {
          options.headers = {
            ...options.headers,
            'X-CSRFToken': newToken
          }
          return fetch(url, options)
        }
      }
    }
    
    return response
  } catch (error) {
    console.error('Secure fetch 失敗:', error)
    throw error
  }
}

/**
 * 初始化 CSRF 保護
 * 應用程式啟動時調用
 */
export async function initCSRFProtection() {
  try {
    await fetchCSRFToken()
    console.log('✓ CSRF 保護已初始化')
  } catch (error) {
    console.error('CSRF 保護初始化失敗:', error)
  }
}

export default {
  fetchCSRFToken,
  getCSRFToken,
  clearCSRFToken,
  ensureCSRFToken,
  secureFetch,
  initCSRFProtection
}
